import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    fuel: 'M3 2v10M3 2h18M21 2v10M3 12h18M12 22V12M17 22h-10',
    tool: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
    dollar: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    settings: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12',
    calendar: ['M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18'],
    trendingUp: 'M23 6l-9.5 9.5-5-5L1 18',
    pieChart: 'M21.21 15.89A10 10 0 1 1 8 2.83M22 12A10 10 0 0 0 12 2v10z',
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3']
};

export default function VehicleCostAnalysis({ user, operatorDetails }) {
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fleetLoading, setFleetLoading] = useState(true);

    const [activeModal, setActiveModal] = useState(null);
    const [quickForm, setQuickForm] = useState({});
    const [dropdownData, setDropdownData] = useState({ workshops: [], services: [], fuelTypes: [], statuses: [] });

    const operatorId = user?.operatorId || user?._id || 'all';
    const country = operatorDetails?.country;

    useEffect(() => {
        fetchVehicles();
        loadDropdownData();
    }, [operatorId]);

    const fetchVehicles = async () => {
        try {
            const res = await axios.get(`${API}/vehicles/${operatorId}`);
            setVehicles(res.data);
        } catch (err) {
            console.error('Fetch vehicles error:', err);
        } finally {
            setFleetLoading(false);
        }
    };

    const loadDropdownData = async () => {
        try {
            const [wRes, mRes] = await Promise.all([
                axios.get(`${API}/workshops/${operatorId}`),
                axios.get(`${API}/master-data`)
            ]);
            setDropdownData({
                workshops: wRes.data,
                services: mRes.data.filter(d => d.category === 'service-types'),
                fuelTypes: mRes.data.filter(d => d.category === 'fuel-types'),
                statuses: mRes.data.filter(d => d.category === 'general-status')
            });
        } catch (err) {
            console.error('Error loading dropdown data:', err);
        }
    };

    useEffect(() => {
        if (selectedVehicle) fetchAnalysis(selectedVehicle._id);
        else setAnalysis(null);
    }, [selectedVehicle]);

    const fetchAnalysis = async (vehicleId) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/vehicle-analysis/${vehicleId}`);
            setAnalysis(res.data);
        } catch (err) {
            console.error('Fetch analysis error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickSave = async (e) => {
        e.preventDefault();
        try {
            let endpoint = '';
            const payload = { ...quickForm, vehicleId: selectedVehicle._id, operatorId };
            if (activeModal === 'fuel') endpoint = '/fuel-records';
            else if (activeModal === 'job') endpoint = '/maintenance-jobs';
            else if (activeModal === 'part') endpoint = '/tyre-batteries';

            await axios.post(`${API}${endpoint}`, payload);
            setActiveModal(null);
            setQuickForm({});
            fetchAnalysis(selectedVehicle._id);
        } catch (err) {
            console.error('Quick save error:', err);
            alert('Failed to save record.');
        }
    };

    const openModal = (type) => {
        setActiveModal(type);
        if (type === 'fuel') {
            setQuickForm({
                date: new Date().toISOString().split('T')[0],
                fuelType: dropdownData.fuelTypes[0]?.name || '',
                quantity: '', cost: '', mileage: ''
            });
        } else if (type === 'job') {
            setQuickForm({
                jobName: '',
                maintenanceDate: new Date().toISOString().split('T')[0],
                serviceType: dropdownData.services[0]?.name || '',
                workshopId: dropdownData.workshops[0]?._id || '',
                estimatedCost: '',
                status: dropdownData.statuses.find(s => s.name.toLowerCase().includes('pending'))?.name || dropdownData.statuses[0]?.name || '',
                notes: ''
            });
        } else if (type === 'part') {
            setQuickForm({ itemName: '', type: 'Tyre', quantity: 1, unitPrice: '', totalPrice: '', condition: 'New', notes: '' });
        }
    };

    const handleExport = () => {
        if (!analysis || !selectedVehicle) return;

        const { summary, logs } = analysis;
        const csvRows = [];

        // Header Information
        csvRows.push(`Vehicle Cost Analysis Report - ${selectedVehicle.plateNumber}`);
        csvRows.push(`Generated on: ${new Date().toLocaleString()}`);
        csvRows.push('');

        // Summary Section
        csvRows.push('SUMMARY STATS');
        csvRows.push(`Total Fuel Cost,${summary.fuelCost}`);
        csvRows.push(`Maintenance Cost,${summary.maintenanceCost}`);
        csvRows.push(`Tyres & Parts Cost,${summary.partsCost}`);
        csvRows.push(`Fixed Monthly Cost,${summary.fixedMonthlyCost || 0}`);
        csvRows.push(`Other Costs,${summary.otherCost || 0}`);
        csvRows.push(`TOTAL LIFECYCLE COST,${(summary.fuelCost || 0) + (summary.maintenanceCost || 0) + (summary.partsCost || 0) + (summary.otherCost || 0) + (summary.fixedMonthlyCost || 0)}`);
        csvRows.push('');

        // Logs Section
        csvRows.push('TRANSACTION TIMELINE');
        csvRows.push('Date,Type,Amount,Notes');

        logs.forEach(log => {
            const date = new Date(log.date).toLocaleDateString();
            const notes = `"${log.notes.replace(/"/g, '""')}"`; // Escape quotes for CSV
            csvRows.push(`${date},${log.type},${log.amount},${notes}`);
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `Cost_Analysis_${selectedVehicle.plateNumber}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const costBreakdown = useMemo(() => {
        if (!analysis) return [];
        const { summary } = analysis;
        const total = (summary.fuelCost || 0) + (summary.maintenanceCost || 0) + (summary.partsCost || 0) + (summary.otherCost || 0) + (summary.fixedMonthlyCost || 0);
        if (total === 0) return [];

        return [
            { label: 'Fuel', value: summary.fuelCost, color: '#f59e0b', p: (summary.fuelCost / total) * 100 },
            { label: 'Maintenance', value: summary.maintenanceCost, color: '#6366f1', p: (summary.maintenanceCost / total) * 100 },
            { label: 'Tyres & Parts', value: summary.partsCost, color: '#10b981', p: (summary.partsCost / total) * 100 },
            { label: 'Fixed Monthly', value: summary.fixedMonthlyCost, color: '#0ea5e9', p: ((summary.fixedMonthlyCost || 0) / total) * 100 },
            { label: 'Others', value: summary.otherCost, color: '#94a3b8', p: (summary.otherCost / total) * 100 }
        ].filter(i => i.value > 0);
    }, [analysis]);

    return (
        <div className="vca-premium">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
                
                .vca-premium { 
                    padding: 32px; background: #f8fafc; min-height: 100vh; 
                    font-family: 'Outfit', sans-serif; color: #1e293b;
                    box-sizing: border-box; width: 100%; overflow-x: hidden;
                }

                .vca-top-bar { 
                    display: flex; justify-content: space-between; align-items: flex-end; 
                    margin-bottom: 32px; gap: 24px; flex-wrap: wrap;
                }
                
                .vca-title-area h1 { font-size: 30px; font-weight: 800; letter-spacing: -0.03em; margin: 0; color: #0f172a; }
                .vca-title-area p { font-size: 14px; color: #64748b; margin: 6px 0 0 0; font-weight: 500; }

                .vca-selector-card {
                    background: white; padding: 12px 20px; border-radius: 20px;
                    border: 1px solid #e2e8f0; box-shadow: 0 4px 20px rgba(0,0,0,0.04);
                    display: flex; align-items: center; gap: 16px; min-width: 350px; max-width: 100%;
                }
                .vca-selector-icon {
                    width: 44px; height: 44px; background: #f1f5f9; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center; color: #6366f1; flex-shrink: 0;
                }
                .vca-selector-content { flex: 1; min-width: 0; }
                .vca-selector-label { font-size: 10px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 2px; }
                .vca-dropdown {
                    width: 100%; border: none; font-size: 16px; font-weight: 700; color: #1e293b; outline: none;
                    background: transparent; padding: 0; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }

                .vca-actions-strip { display: flex; gap: 12px; margin-bottom: 32px; flex-wrap: wrap; }
                .vca-action-pill {
                    padding: 10px 18px; border-radius: 14px; background: white; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 700;
                    color: #475569; cursor: pointer; transition: all 0.2s;
                }
                .vca-action-pill:hover { border-color: #6366f1; color: #6366f1; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
                .vca-action-pill.export { background: #0f172a; color: white; border: none; margin-left: auto; }
                .vca-action-pill.export:hover { background: #1e293b; color: white; transform: translateY(-2px); }

                .vca-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 32px; }
                .vca-stat-box {
                    background: white; padding: 20px; border-radius: 24px; border: 1px solid #e2e8f0;
                    position: relative; overflow: hidden; transition: all 0.3s;
                }
                .vca-stat-box:hover { transform: translateY(-4px); box-shadow: 0 12px 20px -5px rgba(0,0,0,0.08); }
                .vca-stat-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
                .vca-stat-icon { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; }
                .vca-stat-value { font-size: 24px; font-weight: 800; color: #0f172a; margin-bottom: 6px; letter-spacing: -0.02em; line-height: 1; }
                .vca-stat-name { font-size: 13px; font-weight: 600; color: #64748b; line-height: 1.2; }
                .vca-stat-tag { font-size: 10px; font-weight: 800; padding: 4px 8px; border-radius: 6px; text-transform: uppercase; letter-spacing: 0.05em; flex-shrink: 0; }

                .vca-main-layout { display: grid; grid-template-columns: 1fr 320px; gap: 24px; align-items: start; max-width: 100%; }
                @media (max-width: 1200px) { .vca-main-layout { grid-template-columns: 1fr; } }

                .vca-card {
                    background: white; border-radius: 24px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02); overflow: hidden;
                }
                .vca-card-head { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .vca-card-title { font-size: 18px; font-weight: 800; color: #0f172a; }

                .vca-timeline-wrap { padding: 24px; }
                .vca-timeline-item { display: flex; gap: 20px; position: relative; padding-bottom: 28px; }
                .vca-timeline-item::before { content: ''; position: absolute; left: 21px; top: 40px; bottom: 0; width: 2px; background: #f1f5f9; }
                .vca-timeline-item:last-child { padding-bottom: 0; }
                
                .vca-tl-icon-box {
                    width: 42px; height: 42px; border-radius: 12px; background: #fff;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05); border: 1px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: center; z-index: 2; flex-shrink: 0;
                }
                .vca-tl-content { flex: 1; min-width: 0; }
                .vca-tl-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; gap: 10px; }
                .vca-tl-type { font-size: 15px; font-weight: 800; color: #1e293b; }
                .vca-tl-price { font-size: 16px; font-weight: 800; color: #0f172a; flex-shrink: 0; }
                .vca-tl-desc { font-size: 13px; color: #64748b; font-weight: 500; line-height: 1.5; }
                .vca-tl-date { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; margin-top: 8px; display: block; }

                .vca-sidebar-area { min-width: 0; }
                .vca-breakdown-card { padding: 32px 24px; }
                .vca-legend-list { display: flex; flex-direction: column; gap: 16px; margin-top: 8px; }
                .vca-legend-row { display: flex; align-items: flex-start; gap: 12px; }
                .vca-legend-bullet { width: 8px; height: 8px; border-radius: 2px; margin-top: 5px; flex-shrink: 0; }
                .vca-legend-info { flex: 1; min-width: 0; }
                .vca-legend-name { font-size: 13px; font-weight: 700; color: #475569; display: block; margin-bottom: 6px; }
                .vca-legend-bar-bg { height: 7px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
                .vca-legend-bar-fill { height: 100%; border-radius: 4px; transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1); }

                .vca-placeholder { 
                    padding: 120px 40px; text-align: center; background: white; border-radius: 32px; 
                    border: 1px solid #e2e8f0; display: flex; flex-direction: column; align-items: center;
                    justify-content: center; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02);
                }
                .vca-placeholder-icon-wrap {
                    width: 100px; height: 100px; background: #f8fafc; border: 1px solid #e2e8f0;
                    border-radius: 30px; display: flex; align-items: center; justify-content: center;
                    margin-bottom: 32px; color: #6366f1;
                }
                .vca-placeholder h3 { font-size: 24px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0; letter-spacing: -0.02em; }
                .vca-placeholder p { font-size: 16px; color: #64748b; margin: 0; max-width: 400px; line-height: 1.6; font-weight: 500; }

                .vca-info-panel { margin-top: 24px; padding: 20px; background: #f8fafc; border-radius: 18px; border: 1px solid #e2e8f0; }
                .vca-info-title { font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 14px; }
                .vca-info-row { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 12.5px; gap: 10px; }
                .vca-info-label { color: #64748b; font-weight: 500; flex-shrink: 0; }
                .vca-info-val { color: #1e293b; font-weight: 700; text-align: right; word-break: break-all; }

                .vca-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(8px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                    animation: fadeIn 0.3s ease;
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

                .vca-modal-shell {
                    background: white; width: 440px; max-width: 95vw; border-radius: 28px; padding: 0;
                    box-shadow: 0 20px 50px -12px rgba(0,0,0,0.2); border: 1px solid #e2e8f0;
                    overflow: hidden; animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                .vca-modal-head { 
                    padding: 24px 32px; border-bottom: 1px solid #f1f5f9; 
                    display: flex; justify-content: space-between; align-items: center;
                    background: #f8fafc;
                }
                .vca-modal-head h2 { font-size: 19px; font-weight: 800; color: #0f172a; margin: 0; }
                .vca-modal-close { 
                    width: 32px; height: 32px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
                    cursor: pointer; color: #94a3b8; transition: all 0.2s; background: white; border: 1px solid #e2e8f0;
                }
                .vca-modal-close:hover { color: #0f172a; border-color: #cbd5e1; transform: scale(1.1); }
                
                .vca-modal-body { padding: 32px; }

                .vca-field { margin-bottom: 20px; }
                .vca-field label { display: block; font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; margin-bottom: 8px; letter-spacing: 0.08em; }
                .vca-control {
                    width: 100%; padding: 12px 16px; border-radius: 12px; border: 1.5px solid #e2e8f0;
                    font-size: 15px; font-weight: 600; color: #1e293b; transition: all 0.2s; background: #fff;
                    outline: none;
                }
                .vca-control:focus { border-color: #6366f1; box-shadow: 0 0 0 4px rgba(99,102,241,0.08); }
                .vca-control::placeholder { color: #cbd5e1; font-weight: 500; }
                
                .vca-btn-primary {
                    width: 100%; background: #6366f1; color: white; border: none; padding: 14px;
                    border-radius: 14px; font-size: 15px; font-weight: 800; cursor: pointer;
                    box-shadow: 0 10px 15px -3px rgba(99,102,241,0.3); transition: all 0.2s;
                    margin-top: 8px;
                }
                .vca-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 20px -3px rgba(99,102,241,0.4); }

                .loader-ring { width: 48px; height: 48px; border: 4px solid #e2e8f0; border-top: 4px solid #6366f1; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            <div className="vca-top-bar">
                <div className="vca-title-area">
                    <h1>Vehicle Cost Analysis</h1>
                    <p>Deep financial analytics & operational spending breakdown</p>
                </div>
                <div className="vca-selector-card">
                    <div className="vca-selector-icon"><Icon d={icons.truck} size={22} strokeWidth={2.5} /></div>
                    <div className="vca-selector-content">
                        <label className="vca-selector-label">Analyzing Fleet Unit</label>
                        <select className="vca-dropdown" value={selectedVehicle?._id || ''} onChange={(e) => setSelectedVehicle(vehicles.find(v => v._id === e.target.value))}>
                            <option value="">Select a vehicle to begin...</option>
                            {vehicles.map(v => <option key={v._id} value={v._id}>{v.plateNumber} • {v.make} {v.model}</option>)}
                        </select>
                    </div>
                    <Icon d={icons.search} size={18} color="#94a3b8" />
                </div>
            </div>

            {!selectedVehicle ? (
                <div className="vca-placeholder">
                    <div className="vca-placeholder-icon-wrap">
                        <Icon d={icons.pieChart} size={48} strokeWidth={1.5} />
                    </div>
                    <h3>Analyze Your Fleet Performance</h3>
                    <p>Select a vehicle from the dropdown above to generate a comprehensive lifecycle cost analysis and financial breakdown.</p>
                </div>
            ) : loading ? (
                <div className="vca-placeholder">
                    <div className="loader-ring"></div>
                    <h3>Aggregating Fleet Intelligence</h3>
                    <p>Processing financial logs and operational data for vehicle <strong>{selectedVehicle.plateNumber}</strong>...</p>
                </div>
            ) : analysis ? (
                <>
                    <div className="vca-actions-strip">
                        <button className="vca-action-pill" onClick={() => openModal('fuel')}><Icon d={icons.fuel} size={18} strokeWidth={2.5} color="#f59e0b" /> Record Fuel</button>
                        <button className="vca-action-pill" onClick={() => openModal('job')}><Icon d={icons.tool} size={18} strokeWidth={2.5} color="#6366f1" /> Maint. Job</button>
                        <button className="vca-action-pill" onClick={() => openModal('part')}><Icon d={icons.settings} size={18} strokeWidth={2.5} color="#10b981" /> Tyres / Battery</button>
                        
                        <button className="vca-action-pill export" onClick={handleExport}>
                            <Icon d={icons.download} size={16} strokeWidth={2.5} color="white" /> Export Report
                        </button>
                    </div>

                    <div className="vca-stats-grid">
                        {[
                            { label: 'Total Fuel', val: analysis.summary.fuelCost, bg: '#fff7ed', col: '#f59e0b', icon: 'fuel' },
                            { label: 'Maintenance', val: analysis.summary.maintenanceCost, bg: '#eef2ff', col: '#6366f1', icon: 'tool' },
                            { label: 'Tyres & Parts', val: analysis.summary.partsCost, bg: '#ecfdf5', col: '#10b981', icon: 'settings' },
                            { label: 'Fixed Monthly', val: analysis.summary.fixedMonthlyCost, bg: '#f0f9ff', col: '#0ea5e9', icon: 'calendar' },
                            { label: 'Lifecycle Cost', val: (analysis.summary.fuelCost || 0) + (analysis.summary.maintenanceCost || 0) + (analysis.summary.partsCost || 0) + (analysis.summary.otherCost || 0) + (analysis.summary.fixedMonthlyCost || 0), bg: '#6366f1', col: '#fff', icon: 'trendingUp', dark: true }
                        ].map((s, i) => (
                            <div key={i} className="vca-stat-box" style={s.dark ? { background: s.bg, color: s.col } : {}}>
                                <div className="vca-stat-header">
                                    <div className="vca-stat-icon" style={{ background: s.dark ? 'rgba(255,255,255,0.2)' : s.bg, color: s.col }}><Icon d={icons[s.icon]} size={18} strokeWidth={s.dark ? 3 : 2} /></div>
                                    <span className="vca-stat-tag" style={{ background: s.dark ? 'rgba(255,255,255,0.1)' : s.bg, color: s.col }}>{s.label}</span>
                                </div>
                                <div className="vca-stat-value" style={s.dark ? { color: '#fff' } : {}}>{formatCurrency(s.val, country)}</div>
                                <div className="vca-stat-name" style={s.dark ? { color: 'rgba(255,255,255,0.7)' } : {}}>{s.label} Expense</div>
                            </div>
                        ))}
                    </div>

                    <div className="vca-main-layout">
                        <div className="vca-card">
                            <div className="vca-card-head">
                                <div className="vca-card-title">Analysis Timeline</div>
                                <span style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>{analysis.logs.length} Entries</span>
                            </div>
                            <div className="vca-timeline-wrap">
                                {analysis.logs.length === 0 ? <div className="vca-empty-list">No transactions found.</div> : 
                                    analysis.logs.map((log, i) => (
                                        <div key={i} className="vca-timeline-item">
                                            <div className="vca-tl-icon-box" style={{ color: log.icon === 'fuel' ? '#f59e0b' : log.icon === 'tool' ? '#6366f1' : log.icon === 'settings' ? '#10b981' : '#ef4444' }}><Icon d={icons[log.icon]} size={20} strokeWidth={2.5} /></div>
                                            <div className="vca-tl-content">
                                                <div className="vca-tl-top"><span className="vca-tl-type">{log.type}</span><span className="vca-tl-price">{formatCurrency(log.amount, country)}</span></div>
                                                <div className="vca-tl-desc">{log.notes}</div>
                                                <span className="vca-tl-date">{new Date(log.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>

                        <div className="vca-sidebar-area">
                            <div className="vca-card vca-breakdown-card">
                                <div className="vca-card-title" style={{ marginBottom: 24 }}>Spending Mix</div>
                                <div className="vca-legend-list">
                                    {costBreakdown.map((item, i) => (
                                        <div key={i} className="vca-legend-row">
                                            <div className="vca-legend-bullet" style={{ background: item.color }} />
                                            <div className="vca-legend-info">
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span className="vca-legend-name">{item.label}</span>
                                                    <span style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{Math.round(item.p)}%</span>
                                                </div>
                                                <div className="vca-legend-bar-bg"><div className="vca-legend-bar-fill" style={{ width: `${item.p}%`, background: item.color }} /></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="vca-info-panel">
                                    <div className="vca-info-title">Vehicle Highlights</div>
                                    {[
                                        { l: 'Identification', v: selectedVehicle.plateNumber },
                                        { l: 'Unit Model', v: `${selectedVehicle.make} ${selectedVehicle.model}` },
                                        { l: 'Unit Status', v: selectedVehicle.vehicleStatus, col: '#10b981' }
                                    ].map((row, i) => (
                                        <div key={i} className="vca-info-row"><span className="vca-info-label">{row.l}</span><span className="vca-info-val" style={row.col ? { color: row.col } : {}}>{row.v}</span></div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : null}

            {activeModal && (
                <div className="vca-modal-overlay" onClick={() => setActiveModal(null)}>
                    <div className="vca-modal-shell" onClick={e => e.stopPropagation()}>
                        <div className="vca-modal-head">
                            <h2>{activeModal === 'fuel' ? 'Record Fuel Entry' : activeModal === 'job' ? 'New Maintenance Job' : 'Register Parts/Tyres'}</h2>
                            <div className="vca-modal-close" onClick={() => setActiveModal(null)}><Icon d={icons.x} size={20} strokeWidth={3} /></div>
                        </div>
                        <div className="vca-modal-body">
                            <form onSubmit={handleQuickSave}>
                                {activeModal === 'fuel' && (
                                    <>
                                        <div className="vca-field"><label>Transaction Date</label><input type="date" className="vca-control" value={quickForm.date} onChange={e => setQuickForm({ ...quickForm, date: e.target.value })} /></div>
                                        <div className="vca-field"><label>Fuel Type</label><select className="vca-control" value={quickForm.fuelType} onChange={e => setQuickForm({ ...quickForm, fuelType: e.target.value })}>{dropdownData.fuelTypes.map(f => <option key={f._id} value={f.name}>{f.name}</option>)}</select></div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                            <div className="vca-field"><label>Quantity (Ltrs)</label><input type="number" className="vca-control" value={quickForm.quantity} onChange={e => setQuickForm({ ...quickForm, quantity: e.target.value })} placeholder="0.00" /></div>
                                            <div className="vca-field"><label>Total Cost ({country || 'Value'})</label><input type="number" className="vca-control" value={quickForm.cost} onChange={e => setQuickForm({ ...quickForm, cost: e.target.value })} placeholder="0.00" /></div>
                                        </div>
                                        <div className="vca-field"><label>Odometer Reading (km)</label><input type="number" className="vca-control" value={quickForm.mileage} onChange={e => setQuickForm({ ...quickForm, mileage: e.target.value })} placeholder="Current Reading" /></div>
                                    </>
                                )}
                                {activeModal === 'job' && (
                                    <>
                                        <div className="vca-field"><label>Service/Task Name</label><input className="vca-control" placeholder="e.g. Engine Oil Change" value={quickForm.jobName} onChange={e => setQuickForm({ ...quickForm, jobName: e.target.value })} /></div>
                                        <div className="vca-field"><label>Assigned Workshop</label><select className="vca-control" value={quickForm.workshopId} onChange={e => setQuickForm({ ...quickForm, workshopId: e.target.value })}><option value="">Select workshop...</option>{dropdownData.workshops.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}</select></div>
                                        <div className="vca-field"><label>Estimated Service Cost</label><input type="number" className="vca-control" value={quickForm.estimatedCost} onChange={e => setQuickForm({ ...quickForm, estimatedCost: e.target.value })} placeholder="0.00" /></div>
                                        <div className="vca-field"><label>Job Status</label><select className="vca-control" value={quickForm.status} onChange={e => setQuickForm({ ...quickForm, status: e.target.value })}>{dropdownData.statuses.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}</select></div>
                                    </>
                                )}
                                {activeModal === 'part' && (
                                    <>
                                        <div className="vca-field"><label>Part Description</label><input className="vca-control" placeholder="e.g. Front Brake Pads" value={quickForm.itemName} onChange={e => setQuickForm({ ...quickForm, itemName: e.target.value })} /></div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                            <div className="vca-field"><label>Quantity</label><input type="number" className="vca-control" value={quickForm.quantity} onChange={e => setQuickForm({ ...quickForm, quantity: e.target.value })} /></div>
                                            <div className="vca-field"><label>Total Price</label><input type="number" className="vca-control" value={quickForm.totalPrice} onChange={e => setQuickForm({ ...quickForm, totalPrice: e.target.value })} placeholder="0.00" /></div>
                                        </div>
                                        <div className="vca-field"><label>Condition</label><select className="vca-control" value={quickForm.condition} onChange={e => setQuickForm({ ...quickForm, condition: e.target.value })}><option value="New">Brand New</option><option value="Used">Used / Refurbished</option></select></div>
                                    </>
                                )}
                                <button type="submit" className="vca-btn-primary">Finalize and Post Entry</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
